import type { FieldPath } from 'react-hook-form';

import type { Task, TaskType } from '../../types/interfaces';

type TaskCreateValidationError = {
  field: FieldPath<Task>;
  message: string;
};

const REQUIRED_MESSAGES: Partial<Record<FieldPath<Task>, string>> = {
  name: 'Пожалуйста, укажите название наряда',
  taskType: 'Выберите тип наряда.',
  asset: 'Пожалуйста, укажите объект',
  department: 'Пожалуйста, укажите участок',
  employees: 'Данному наряду должен быть назначен исполнитель',
  plannedStartDate: 'Пожалуйста, укажите начало работы (план)',
  actualStartDate: 'Пожалуйста, укажите начало работы (факт)',
  plannedEndDate: 'Пожалуйста, укажите завершение работы (план)',
  actualEndDate: 'Пожалуйста, укажите завершение работы (факт)',
  description: 'Пожалуйста, укажите описание',
  plannedOperations: 'Пожалуйста, укажите плановые работы',
  actualOperations: 'Пожалуйста, укажите фактические работы'
};

const hasText = (value: unknown): boolean =>
  typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;

const hasSelection = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false;
  const key = (value as { key?: unknown; id?: unknown }).key ?? (value as { id?: unknown }).id;
  return key !== null && key !== undefined && String(key).trim().length > 0;
};

const hasArrayItems = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0;

function addRequiredError(
  errors: TaskCreateValidationError[],
  field: FieldPath<Task>,
  isValid: boolean
) {
  if (isValid) return;
  errors.push({
    field,
    message: REQUIRED_MESSAGES[field] ?? 'Пожалуйста, заполните обязательное поле'
  });
}

export function validateTaskCreateByTaskType(
  task: Task,
  taskTypeDetails: TaskType | null | undefined
): TaskCreateValidationError[] {
  const errors: TaskCreateValidationError[] = [];
  const requiredFields = taskTypeDetails?.requiredFields;

  addRequiredError(errors, 'taskType', hasSelection(task.taskType));

  if (!requiredFields) return errors;

  addRequiredError(errors, 'name', !requiredFields.name || hasText(task.name));
  addRequiredError(errors, 'asset', !requiredFields.asset || hasSelection(task.asset));
  addRequiredError(
    errors,
    'department',
    !requiredFields.department ||
      taskTypeDetails.assignDepartmentOnSave ||
      hasSelection(task.department)
  );
  addRequiredError(
    errors,
    'employees',
    !requiredFields.employees || hasArrayItems(task.employees)
  );
  addRequiredError(
    errors,
    'plannedStartDate',
    !requiredFields.plannedStartDate || hasText(task.plannedStartDate)
  );
  addRequiredError(
    errors,
    'actualStartDate',
    !requiredFields.actualStartDate || hasText(task.actualStartDate)
  );
  addRequiredError(
    errors,
    'plannedEndDate',
    !requiredFields.plannedEndDate || hasText(task.plannedEndDate)
  );
  addRequiredError(
    errors,
    'actualEndDate',
    !requiredFields.actualEndDate || hasText(task.actualEndDate)
  );
  addRequiredError(
    errors,
    'description',
    !requiredFields.description || hasText(task.description)
  );
  addRequiredError(
    errors,
    'plannedOperations',
    !requiredFields.plannedOperations || hasArrayItems(task.plannedOperations)
  );
  addRequiredError(
    errors,
    'actualOperations',
    !requiredFields.actualOperations || hasArrayItems(task.actualOperations)
  );

  return errors;
}
